import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { submitContact } from '../mockData';

export const ContactSectionDark = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Save contact message to backend
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Error al enviar el mensaje');
      }

      toast.success('¡Mensaje enviado!', {
        description: 'Nos pondremos en contacto contigo pronto.'
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Error al enviar', {
        description: 'Por favor inténtalo de nuevo.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contacto" className="py-20 bg-black relative overflow-hidden">
      {/* Blurred fries background */}
      <div 
        className="fries-bg-blur" 
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/115740/pexels-photo-115740.jpeg)'
        }}
      ></div>
      
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-red-950/20 to-black"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            ¿Tienes <span className="bg-gradient-to-r from-orange-500 to-red-600 bg-clip-text text-transparent">Preguntas?</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Contáctanos y con gusto te ayudaremos
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold mb-6 text-white">Información de Contacto</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4 group">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg shadow-red-900/50">
                    <Phone className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">WhatsApp</h4>
                    <a href="https://wa.me/528771228589" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-orange-500 transition-colors">
                      877 177 8264
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4 group">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg shadow-red-900/50">
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Email</h4>
                    <a href="mailto:mrcrunch2friedchicken@gmail.com" className="text-gray-400 hover:text-orange-500 transition-colors break-all">
                      mrcrunch2friedchicken@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4 group">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg shadow-red-900/50">
                    <MapPin className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Ubicación</h4>
                    <p className="text-gray-400">Calle Luis G. Urbina #1490</p>
                    <p className="text-gray-400">Colonia 28 de Junio</p>
                    <p className="text-gray-400">Cd. Acuña, Coahuila, México</p>
                    <div className="mt-2 space-y-1">
                      <p className="text-gray-400 font-semibold">Horarios:</p>
                      <p className="text-gray-400">Jue-Vie: 5:00 PM - 10:00 PM</p>
                      <p className="text-gray-400">Sáb-Dom: 12:00 PM - 10:00 PM</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Google Maps Embed */}
            <div className="rounded-2xl overflow-hidden border-2 border-gray-800 shadow-lg shadow-red-900/20">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3474.5!2d-100.9319!3d29.3244!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x866dd9a6c1f36e7f%3A0x0!2sColonia%2028%20de%20Junio%2C%20Cd%20Acu%C3%B1a%2C%20Coahuila!5e0!3m2!1ses-419!2smx!4v1702900000000!5m2!1ses-419!2smx"
                width="100%"
                height="256"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicación Mr. Crunch 28"
                className="grayscale hover:grayscale-0 transition-all duration-500"
              ></iframe>
              <a 
                href="https://www.google.com/maps/search/Calle+Luis+G.+Urbina+1490,+Colonia+28+de+Junio,+Ciudad+Acuña,+Coahuila,+Mexico"
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-gradient-to-r from-orange-500 to-red-600 text-white text-center py-3 font-semibold hover:from-orange-600 hover:to-red-700 transition-all"
              >
                <MapPin className="inline-block h-5 w-5 mr-2" />
                Abrir en Google Maps
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl shadow-xl shadow-red-900/20 p-8 border-2 border-gray-800">
            <h3 className="text-2xl font-bold mb-6 text-white">Envíanos un Mensaje</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-gray-300">Nombre Completo</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Tu nombre"
                  required
                  className="mt-2 bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-orange-500"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-gray-300">Correo Electrónico</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="tu@email.com"
                  required
                  className="mt-2 bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-orange-500"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-gray-300">Teléfono</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1 (555) 123-4567"
                  className="mt-2 bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-orange-500"
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-gray-300">Mensaje</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="¿En qué podemos ayudarte?"
                  rows={4}
                  required
                  className="mt-2 bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-orange-500"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-lg py-6 shadow-lg shadow-red-900/50"
              >
                {isSubmitting ? 'Enviando...' : (
                  <>
                    Enviar Mensaje
                    <Send className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
